import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, Check, ChevronLeft, CalendarDays, Sparkles, Loader2 } from 'lucide-react';
import { fetchServices, fetchSlots, createAppointment, bookingCheckout } from '../api';
import { fmtTime } from '../mock';
import { useToast } from '../hooks/use-toast';

const STEPS = ['Service', 'Date & Time', 'Your Details', 'Review'];

const buildDays = () => {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= 21; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    out.push(d);
  }
  return out;
};

const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Book = () => {
  const [params] = useSearchParams();
  const { toast } = useToast();
  const days = useMemo(buildDays, []);
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState(params.get('service') || '');
  const [date, setDate] = useState(null);
  const [time, setTime] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [details, setDetails] = useState({ name: '', email: '', phone: '', notes: '' });
  const [variant, setVariant] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { fetchServices().then(setServices).catch(() => {}); }, []);

  const service = services.find((s) => s.id === serviceId);
  const hasVariants = service?.variants && service.variants.length > 0;
  const selectedVariant = hasVariants ? service.variants.find((v) => v.key === variant) : null;
  // Default-select the first variant when a variant service is chosen
  useEffect(() => {
    if (hasVariants && !variant) setVariant(service.variants[0].key);
    if (!hasVariants && variant) setVariant('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);
  const payMode = service?.paymentMode;
  const requiresPay = payMode === 'deposit' || payMode === 'full';
  const basePrice = selectedVariant ? Number(selectedVariant.price) : Number(service?.price || 0);
  const payAmount = !service ? 0 : payMode === 'deposit' ? (Number(service.depositAmount) > 0 ? Number(service.depositAmount) : Math.round(basePrice * 0.5 * 100) / 100) : basePrice;

  useEffect(() => {
    if (step === 1 && serviceId && date) {
      setSlotsLoading(true);
      setTime('');
      fetchSlots(serviceId, toISODate(date)).then((r) => setSlots(r.slots || [])).catch(() => setSlots([])).finally(() => setSlotsLoading(false));
    }
  }, [step, serviceId, date]);

  const canNext = (step === 0 && serviceId) || (step === 1 && date && time) || (step === 2 && details.name && details.email);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const appt = await createAppointment({ service_id: serviceId, date: toISODate(date), time, variant: variant || null, ...details });
      if (requiresPay) {
        const co = await bookingCheckout({ appointment_id: appt.id, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      }
      setDone(appt);
      toast({ title: 'Appointment requested \u2728', description: appt.status === 'confirmed' ? 'Your appointment is confirmed!' : 'We will confirm your booking by email.' });
    } catch (e) {
      const msg = e?.response?.status === 409 ? 'That slot was just taken. Please pick another time.' : 'Could not submit booking. Please try again.';
      toast({ title: 'Booking error', description: msg });
      if (e?.response?.status === 409) { setStep(1); }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-40 min-h-screen">
        <div className="max-w-lg mx-auto px-5 text-center card-glass rounded-3xl p-12">
          <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-br from-[#d6197d] to-[#ff4da6] flex items-center justify-center mb-6"><Check className="h-8 w-8 text-white" /></div>
          <h1 className="font-cinzel text-3xl text-gold-gradient">{done.status === 'confirmed' ? 'Booking Confirmed' : 'Request Received'}</h1>
          <p className="font-cormorant text-xl text-[#f5ecd9]/75 mt-4">Thank you, {done.name}. Your {done.status === 'confirmed' ? 'appointment' : 'request'} for <span className="text-[#f0d582]">{done.service_name}</span> on {done.date} at {fmtTime(done.time)} has been {done.status === 'confirmed' ? 'confirmed' : 'received'}.</p>
          <p className="font-jost text-xs text-[#f5ecd9]/45 mt-6">A confirmation has been sent to {done.email}. {done.status === 'pending' && 'We will confirm availability shortly.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-3xl mx-auto px-5">
        <div className="text-center mb-8">
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">Book Online</p>
          <h1 className="font-cinzel text-4xl md:text-5xl text-gold-gradient">Book A Healing</h1>
          <div className="gold-divider mt-4" />
        </div>

        <div className="flex items-center justify-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${i <= step ? 'text-[#f0d582]' : 'text-[#f5ecd9]/30'}`}>
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-jost ${i <= step ? 'bg-gradient-to-br from-[#d6197d] to-[#ff4da6] text-white' : 'border border-[#f5ecd9]/20'}`}>{i + 1}</span>
                <span className="hidden sm:inline text-xs uppercase tracking-widest">{s}</span>
              </div>
              {i < STEPS.length - 1 && <span className={`h-px w-6 ${i < step ? 'bg-[#d4af37]' : 'bg-[#f5ecd9]/15'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="card-glass rounded-2xl p-6 md:p-8">
          {step === 0 && (
            <div>
              <h2 className="font-cinzel text-xl text-[#f5ecd9] mb-5">Choose a service</h2>
              {services.length === 0 && <p className="font-jost text-sm text-[#f5ecd9]/50">Loading services…</p>}
              <div className="space-y-3">
                {services.map((s) => (
                  <button key={s.id} data-testid={`booking-service-${s.id}`} onClick={() => setServiceId(s.id)} className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all ${serviceId === s.id ? 'border border-[#ff4da6] bg-[#d6197d]/10' : 'border border-[#d4af37]/20 hover:border-[#d4af37]/50'}`}>
                    <img src={s.image} alt={s.name} className="h-14 w-14 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex justify-between"><span className="font-cinzel text-[#f5ecd9]">{s.name}</span><span className="font-cinzel text-gold-gradient">{s.priceLabel}</span></div>
                      <span className="flex items-center gap-1.5 text-xs text-[#f5ecd9]/50 font-jost mt-1"><Clock className="h-3 w-3" /> {s.durationMin} min · {s.autoConfirm ? 'Instant confirm' : 'Request to book'}</span>
                    </div>
                  </button>
                ))}
              </div>
              {hasVariants && (
                <div data-testid="booking-variant-choice" className="mt-5 rounded-xl border border-[#d4af37]/25 bg-[#d4af37]/5 p-4">
                  <p className="font-jost text-xs uppercase tracking-widest text-[#f0d582] mb-3">Choose your {service.name} option</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {service.variants.map((v) => (
                      <button key={v.key} data-testid={`booking-variant-${v.key}`} onClick={() => setVariant(v.key)} className={`flex items-center justify-between p-3 rounded-lg text-left transition-all ${variant === v.key ? 'border border-[#ff4da6] bg-[#d6197d]/10' : 'border border-[#d4af37]/20 hover:border-[#d4af37]/50'}`}>
                        <span className="font-cormorant text-lg text-[#f5ecd9]">{v.label}</span>
                        <span className="font-cinzel text-gold-gradient">{v.priceLabel}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-cinzel text-xl text-[#f5ecd9] mb-1">Choose a date & time</h2>
              <p className="font-jost text-xs text-[#f5ecd9]/40 mb-5 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Availability shown for {service?.name}. Booked slots are hidden.</p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-6">
                {days.map((d) => {
                  const sel = date && d.toDateString() === date.toDateString();
                  return (
                    <button key={d.toISOString()} data-testid={`booking-date-${toISODate(d)}`} onClick={() => setDate(d)} className={`py-3 rounded-lg text-center transition-all ${sel ? 'bg-gradient-to-br from-[#d6197d] to-[#ff4da6] text-white' : 'border border-[#d4af37]/20 text-[#f5ecd9]/70 hover:border-[#d4af37]/50'}`}>
                      <div className="text-[10px] uppercase font-jost opacity-70">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
                      <div className="font-cinzel text-lg">{d.getDate()}</div>
                      <div className="text-[10px] font-jost opacity-70">{d.toLocaleDateString(undefined, { month: 'short' })}</div>
                    </button>
                  );
                })}
              </div>
              {date && (
                slotsLoading ? (
                  <div className="flex items-center gap-2 text-[#f5ecd9]/50 font-jost text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Checking availability…</div>
                ) : slots.length === 0 ? (
                  <p className="font-cormorant text-lg text-[#f5ecd9]/50">No availability on this day. Please choose another date.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {slots.map((sl) => (
                      <button key={sl.time} data-testid={`booking-slot-${sl.time}`} disabled={!sl.available} onClick={() => setTime(sl.time)} className={`py-2.5 rounded-lg text-sm font-jost transition-all ${time === sl.time ? 'bg-gradient-to-br from-[#d6197d] to-[#ff4da6] text-white' : sl.available ? 'border border-[#d4af37]/20 text-[#f5ecd9]/70 hover:border-[#d4af37]/50' : 'border border-white/5 text-[#f5ecd9]/20 line-through cursor-not-allowed'}`}>{fmtTime(sl.time)}</button>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-cinzel text-xl text-[#f5ecd9] mb-2">Your details</h2>
              {[{ k: 'name', l: 'Full Name', t: 'text' }, { k: 'email', l: 'Email', t: 'email' }, { k: 'phone', l: 'Phone (optional)', t: 'tel' }].map((f) => (
                <div key={f.k}>
                  <label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">{f.l}</label>
                  <input type={f.t} value={details[f.k]} onChange={(e) => setDetails({ ...details, [f.k]: e.target.value })} className="mt-2 w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] transition-colors" />
                </div>
              ))}
              <div>
                <label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">Notes (optional)</label>
                <textarea rows={3} value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} className="mt-2 w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] transition-colors resize-none" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-cinzel text-xl text-[#f5ecd9] mb-5">Review your appointment</h2>
              <div className="space-y-3 font-jost text-sm">
                {[['Service', selectedVariant ? `${service?.name} — ${selectedVariant.label}` : service?.name], ['Price', selectedVariant ? selectedVariant.priceLabel : service?.priceLabel], ['Duration', `${service?.durationMin} min`], ['Date', date?.toLocaleDateString()], ['Time', fmtTime(time)], ['Name', details.name], ['Email', details.email], ['Phone', details.phone || '—']].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 pb-2"><span className="text-[#f5ecd9]/50">{k}</span><span className="text-[#f5ecd9]">{v}</span></div>
                ))}
              </div>
              <p className="font-jost text-xs text-[#f5ecd9]/40 mt-4 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#f0d582]" /> {service?.autoConfirm ? 'This booking will be confirmed instantly.' : 'This is a booking request — we\u2019ll confirm availability by email.'}</p>
              {requiresPay && (
                <div data-testid="booking-payment-note" className="mt-4 rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/5 p-4">
                  <div className="flex justify-between font-jost text-sm"><span className="text-[#f5ecd9]/70">{payMode === 'deposit' ? 'Deposit due now' : 'Payment due now'}</span><span className="font-cinzel text-gold-gradient">${payAmount.toFixed(2)}</span></div>
                  {payMode === 'deposit' && <p className="font-jost text-[11px] text-[#f5ecd9]/45 mt-1">A deposit secures your appointment; the balance of ${(basePrice - payAmount).toFixed(2)} is due at your session.</p>}
                  <p className="font-jost text-[11px] text-[#f5ecd9]/45 mt-1">You'll be taken to secure Stripe checkout to complete payment.</p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} className={`flex items-center gap-1 text-sm font-jost text-[#f5ecd9]/60 hover:text-[#f0d582] transition-colors ${step === 0 ? 'invisible' : ''}`}><ChevronLeft className="h-4 w-4" /> Back</button>
            {step < 3 ? (
              <button disabled={!canNext} onClick={() => setStep((s) => s + 1)} className={`btn-gold rounded-full px-8 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37] ${!canNext ? 'opacity-40 cursor-not-allowed' : ''}`}>Continue</button>
            ) : (
              <button data-testid="booking-submit-btn" onClick={submit} disabled={submitting} className="btn-gold rounded-full px-8 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37] disabled:opacity-60">{submitting ? 'Submitting…' : requiresPay ? `Pay $${payAmount.toFixed(2)} & Book` : 'Request Booking'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Book;
